import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Animated, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { BMPrimitives, useBMTheme } from '../../theme';
import { DRNUtil } from '@traveloka/district-util';
/* -------------------------------------------------------------------------- */
/*                                 Stylesheet                                 */
/* -------------------------------------------------------------------------- */
const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
    button: {
        // @ts-ignore
        outlineStyle: 'none',
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: BMPrimitives.BMRadius.rounded,
        display: 'flex',
    },
    buttonBorder: {
        borderRadius: BMPrimitives.BMRadius.rounded,
        position: 'absolute',
        top: -3,
        left: -3,
        zIndex: -10,
    },
});
/**
 * There is a difference in behavior between BMButton and Button used in BMButtonGroup.
 * When hovering BMButton, the bgColor changes. while the textColor changes in BMButtonGroup.
 * No override behavior is done on BMButton to prevent careless use.
 * @returns
 */
function BMButtonGroupItem({ value, label, TrailingIcon, ActiveTrailingIcon, active, onPress, testID, }) {
    /* -------------------------------------------------------------------------- */
    /*                                    Theme                                   */
    /* -------------------------------------------------------------------------- */
    const { BMComponents, BMPrimitives } = useBMTheme();
    const { BMUiBtngroupBgColorInactive, BMUiBtngroupBgColorSelected, BMUiBtngroupInkSelected, BMUiBtngroupInkInactive, BMStyleLineBordercolorActive, } = BMComponents;
    /* -------------------------------------------------------------------------- */
    /*                                    Style                                   */
    /* -------------------------------------------------------------------------- */
    const baseTextColor = active ? BMUiBtngroupInkSelected : BMUiBtngroupInkInactive;
    const animatedTextColor = !active ? BMUiBtngroupInkSelected : BMUiBtngroupInkInactive;
    const baseButtonStyle = {
        backgroundColor: active ? BMUiBtngroupBgColorSelected : BMUiBtngroupBgColorInactive,
        paddingHorizontal: BMComponents.BMUiButtonHmarginLarge - BMPrimitives.BMStroke.lg,
        paddingVertical: BMComponents.BMUiButtonHmarginSmall - BMPrimitives.BMStroke.lg,
    };
    const baseButtonTextStyle = {
        ...BMPrimitives.BMTypography.uiNormal,
    };
    const [buttonSize, setButtonSize] = useState({ height: 0, width: 0 });
    const onLayout = (event) => {
        const { height, width } = event.nativeEvent.layout;
        setButtonSize({ height, width });
    };
    /* -------------------------------------------------------------------------- */
    /*                                  Animation                                 */
    /* -------------------------------------------------------------------------- */
    const hoverAnimation = useRef(new Animated.Value(0)).current;
    const borderColorAnimation = useRef(new Animated.Value(0)).current;
    const hoverAnimationInterpolation = hoverAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [baseTextColor, animatedTextColor],
    });
    const borderColorAnimationInterpolation = borderColorAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: ['transparent', BMStyleLineBordercolorActive],
    });
    /* -------------------------------------------------------------------------- */
    /*                                 Interaction                                */
    /* -------------------------------------------------------------------------- */
    const instantTimimg = BMPrimitives.BMAnimation.duration.quick;
    const getAnimationConfig = (toValue) => ({
        toValue,
        duration: instantTimimg,
        useNativeDriver: false,
    });
    const handleHoverIn = () => {
        Animated.timing(hoverAnimation, getAnimationConfig(1)).start();
    };
    const handleHoverOut = () => {
        Animated.timing(hoverAnimation, getAnimationConfig(0)).start();
    };
    const handlePressIn = () => {
        Animated.timing(borderColorAnimation, getAnimationConfig(1)).start();
    };
    const handlePressOut = () => {
        Animated.timing(borderColorAnimation, getAnimationConfig(0)).start();
    };
    const Icon = active && !!ActiveTrailingIcon ? ActiveTrailingIcon : TrailingIcon;
    useEffect(() => {
        // To prevent missmatch button color when state is changed
        handleHoverOut();
    }, [active]);
    return (<Pressable style={styles.button} onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} onPressIn={handlePressIn} onPressOut={handlePressOut} onPress={() => onPress(value)} testID={`${testID}-${value}`}>
      <Animated.View style={[
            styles.buttonContainer,
            baseButtonStyle,
            // @ts-ignore 'fit-content' can't be value of width anymore as the type became more specific now
            DRNUtil.isWeb() ? { width: 'fit-content' } : { alignSelf: 'flex-start' },
        ]} onLayout={onLayout}>
        <Animated.Text style={[baseButtonTextStyle, { color: hoverAnimationInterpolation }]} accessibilityLabel={label}>
          {label}
        </Animated.Text>
        {!!Icon ? (<View style={{ marginLeft: BMPrimitives.BMSize.xs }} testID={`${testID}-${value}-icon`}>
            <Icon height="16" width="16" accentColor={BMComponents.BMUiButtonInkSecondary}/>
          </View>) : null}
      </Animated.View>

      {/* pseudo border  */}
      <Animated.View style={[
            styles.buttonBorder,
            {
                backgroundColor: borderColorAnimationInterpolation,
                width: buttonSize.width + 6,
                height: buttonSize.height + 6,
                // padding: 3,
            },
        ]}/>
    </Pressable>);
}
function BMButtonGroupWrapper({ singleRow, children, style, testID, }) {
    if (singleRow) {
        return (<ScrollView horizontal showsHorizontalScrollIndicator={false} style={style} testID={`${testID}-singlerow-container`}>
        {children}
      </ScrollView>);
    }
    return (<View style={[
            style,
            { flexWrap: 'wrap' },
            {
                marginHorizontal: -(BMPrimitives.BMSize.md / 2),
                marginVertical: -(BMPrimitives.BMSize.md / 2),
            },
        ]} testID={`${testID}-wrap-container`}>
      {children}
    </View>);
}
/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */
/**
 *
 * @param {BMButtonGroupProps}
 * @returns
 */
const BMButtonGroup = forwardRef(({ initialValues = [], items, onChange, singleRow, multipleValue, testID }, ref) => {
    const initial = useRef(initialValues);
    const [values, setValues] = useState([]);
    useEffect(() => {
        setValues(initialValues);
    }, []);
    useImperativeHandle(ref, () => ({
        reset() {
            onChange(initial.current);
            setValues(initial.current);
        },
        clear() {
            onChange([]);
            setValues([]);
        },
    }));
    const isValueActive = (value) => {
        return multipleValue ? values.includes(value) : values[0] === value;
    };
    const handleClickItem = (value) => {
        let newValues;
        if (multipleValue) {
            if (isValueActive(value)) {
                newValues = values.filter((v) => v !== value);
            }
            else {
                newValues = [...values, value];
            }
        }
        else {
            if (!isValueActive(value)) {
                newValues = [value];
            }
            else {
                newValues = [];
            }
        }
        onChange(newValues);
        setValues(newValues);
    };
    return (<BMButtonGroupWrapper style={styles.container} singleRow={singleRow} testID={testID}>
        <>
          {items.map((item, i) => (<View key={item.value} style={{
                marginHorizontal: BMPrimitives.BMSize.md / 2,
                marginVertical: BMPrimitives.BMSize.md / 2,
            }}>
              <BMButtonGroupItem {...item} onPress={handleClickItem} active={isValueActive(item.value)} testID={testID}/>
            </View>))}
        </>
      </BMButtonGroupWrapper>);
});
export default BMButtonGroup;
